import { useGetDetailUnitMutation } from '@/modules/maps/services/homeMapService'
import { DetailUnit, UnitsMapType } from '@/modules/maps/types/unitsMapType'
import { getBalloonHtml, TBalloonHtml } from '@/modules/maps/utils/balloons'
import { useMediaQuery } from '@mui/material'
import { Placemark } from '@pbe/react-yandex-maps'
import React, { FC, useState } from 'react'
import { getIconForUnit } from '@/components/Organisms/Map/Map'
import iconProc from '@/assets/img/processor.png'
import iconSignal100 from '@/assets/img/full-connection.png'
import iconSignal50 from '@/assets/img/medium-connection.png'
import iconSignal0 from '@/assets/img/no-connection.png'
import iconBattary0 from '@/assets/img/low-battery.png'
import iconBattary50 from '@/assets/img/medium-battery.png'
import iconBattary100 from '@/assets/img/full-battery.png'
import iconElectrical from '@/assets/img/electricalBlue.png'
import iconSameDirectionTraffic from '@/assets/img/icons/sortingUp.png'
import iconOncomingTraffic from '@/assets/img/icons/sortingDown.png'
import iconTotalTraffic from '@/assets/img/icons/sortingDouble.png'

import { signal, voltage12, voltage24, VoltageEnum } from '@/utils'
import { DeviceType } from '@/types/enums'

interface Props {
  unit: UnitsMapType
  yamap: any
}

const PlacemarkBalloon: FC<Props> = ({ unit, yamap }) => {
  const isMobile = useMediaQuery('(max-width:767px)')
  const [getDetailUnit, { isLoading, isError, error }] =
    useGetDetailUnitMutation()
  const [unitData, setUnitData] = useState<DetailUnit>({
    id: 0,
    name: '',
    address: '',
    connectionStatus: 3,
    countModules: 0,
    signal: 0,
    smart: false,
    is220v: false,
    type: '',
    voltage: 0,
    typeId: 0,
    state: 1,
    errorMessage: '',
    ultraShortTermForecastId: 0,
  })

  const onLoadData = async (id: number) => {
    try {
      const res = await getDetailUnit(id).unwrap()
      if (Object.values(res).length > 0) {
        setUnitData(() => res)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const connectionStatus =
    unitData.typeId === DeviceType.kao ||
    unitData.typeId === DeviceType.ksodd ||
    unitData.typeId === DeviceType.trafficLight
      ? null
      : unitData?.connectionStatus

  const balloonData: TBalloonHtml = {
    isLoading,
    isError,
    title: `${unitData.type} ${unitData?.name}`,
    titleLink: `/devices/${unitData?.id}`,
    description: unitData?.address,
    connectionStatus: connectionStatus,
    imgUrl: unitData.imageUrl,
  }
  if (
    unitData.typeId === DeviceType.meteo &&
    unitData?.connectionStatus !== 3
  ) {
    const voltageSchema =
      (unitData?.voltage ?? 0) <= VoltageEnum.min ? voltage12 : voltage24
    const signalText = signal(unitData?.signal)
    const voltageText = voltageSchema(unitData?.voltage)
    let signalIcon = iconSignal0
    let iconVoltage = iconBattary0
    if (signalText === 'medium') {
      signalIcon = iconSignal50
    }
    if (signalText === 'high') {
      signalIcon = iconSignal100
    }
    if (voltageText === 'medium') {
      iconVoltage = iconBattary50
    }
    if (voltageText === 'high') {
      iconVoltage = iconBattary100
    }
    if (unitData?.is220v) {
      iconVoltage = iconElectrical
    }
    balloonData.icons = [
      {
        text: `${unitData?.countModules}`,
        helperText: `Количество модулей в системе`,
        iconSrc: iconProc,
      },
      {
        text: `${unitData?.signal}`,
        helperText: `Уровень GSM сигнала`,
        iconSrc: signalIcon,
      },
      {
        text: `${unitData?.voltage.toFixed(2)} В`,
        helperText: `Напряжение питания`,
        iconSrc: iconVoltage,
      },
    ]
  }

  if (unitData?.typeId === DeviceType.detector) {
    balloonData.icons = [
      {
        text: `${unitData?.sameDirectionDetections}`,
        helperText: `Попутное движение`,
        iconSrc: iconSameDirectionTraffic,
      },
      {
        text: `${unitData?.oppositeDirectionDetections}`,
        helperText: `Встречное движение`,
        iconSrc: iconOncomingTraffic,
      },
      {
        text: `${
          (unitData?.oppositeDirectionDetections ?? 0) +
          (unitData?.sameDirectionDetections ?? 0)
        }`,
        helperText: `Всего`,
        iconSrc: iconTotalTraffic,
      },
    ]
  }

  return (
    <Placemark
      defaultGeometry={unit.coordinates}
      onClick={() => {
        yamap.current.setCenter(
          unit.coordinates,
          yamap.current._zoom > 10 ? yamap.current._zoom : 10,
          {
            checkZoomRange: true,
            duration: 500,
          }
        )
        onLoadData(unit.id)
      }}
      options={{
        iconLayout: 'default#image',
        iconImageHref: getIconForUnit(unit),
        iconImageSize: [40, 40],
        openBalloonOnClick: true,
        hideIconOnBalloonOpen: false,
        balloonOffset: [-70, -34],
        balloonMaxWidth: isMobile ? 257 : 400,
      }}
      properties={{
        id: unit.id,
        balloonContentBody: getBalloonHtml(balloonData),
        clusterCaption: `
                <div style='display: flex; width: 100px; justify-content: space-between; align-items: center; gap: 10px'>
                  <div>${unit.name}</div>
                  <div style='background-color: ${
                    !unit.smart
                      ? '#7d8eff'
                      : unit.status === 1
                        ? 'green'
                        : unit.status === 2
                          ? 'yellow'
                          : unit.status === 3
                            ? 'red'
                            : 'black'
                  }; width: 10px; height: 10px; transform: ${!unit.smart ? 'rotate(45deg)' : 'unset'}; border-radius: ${unit.smart ? '50%' : '0'};'></div>
                </div>`,
        clusterHideIconOnBalloonOpen: false,
        geoObjectHideIconOnBalloonOpen: false,
      }}
    />
  )
}

export default PlacemarkBalloon
